import type { AskUserQuestion, Message, MessageSegment, ToolApproval } from './flora.type';

/** Copy shown on a re-armed card when a rejection frame carries no message. */
const PROMPT_REJECTION_FALLBACK = 'Flora couldn’t accept that response. Please try again.';

/**
 * An answer or approval sent over a socket and not yet acknowledged. A
 * `{type:"error"}` on the same socket while this is set is the backend refusing
 * the submit (not a failed run), so the prompt is rolled back instead.
 */
interface PendingPromptSubmit {
  jobId: string;
  kind: 'answer' | 'approval';
  /** The socket the submit went out on; only its frames can settle it. */
  socket: WebSocket;
  /** The optimistic `user_answers` segment appended for an answer. */
  answersSegment?: MessageSegment & { type: 'user_answers' };
  /** The questions the answer was for, restored on rejection. */
  questions?: AskUserQuestion[];
  /** The approval that was decided, restored on rejection. */
  toolApproval?: ToolApproval;
}

/** The socket-frame fields that decide whether a pending submit was accepted. */
interface PromptSubmitFrame {
  type?: unknown;
  job_id?: unknown;
  is_start?: unknown;
  is_update?: unknown;
  is_tool_start?: unknown;
  is_done?: unknown;
}

/**
 * Reports whether a socket frame confirms that the backend accepted the pending
 * submit: its explicit ack (`answer_submitted` / `tool_approval_submitted`), or
 * the resumed run starting to stream.
 *
 * @param {PromptSubmitFrame} data - The parsed socket frame
 * @param {PendingPromptSubmit} pending - The submit awaiting an ack
 * @returns {boolean} True when the submit can no longer be rejected
 *
 * @example
 * isPromptSubmitAck({ type: 'answer_submitted', job_id: 'job-1' }, pending); // true
 */
const isPromptSubmitAck = (data: PromptSubmitFrame, pending: PendingPromptSubmit): boolean => {
  if (data.type === 'answer_submitted' || data.type === 'tool_approval_submitted') {
    return data.job_id === undefined || data.job_id === pending.jobId;
  }
  return Boolean(data.is_start || data.is_update || data.is_tool_start || data.is_done);
};

/**
 * Finds the message that owns a job's pending prompt, newest first, so a
 * submit updates the message whose card was clicked rather than whatever
 * message happens to be last.
 *
 * @param {Message[]} messages - The conversation's assistant messages
 * @param {string} jobId - The job the prompt belongs to
 * @returns {number} The message index, or -1 when no message holds that job's prompt
 */
const findPromptMessageIndex = (messages: Message[], jobId: string): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.pendingJobId === jobId || message.pendingToolApproval?.jobId === jobId) return i;
  }
  return -1;
};

/**
 * Applies a replayed `{type:"user_answers"}` frame: the answers the user
 * already gave to the last message's question card. Appends the segment (so
 * the card renders answered) and clears the question slot, so the replayed
 * card can't be answered a second time.
 *
 * @param {Message[]} messages - The conversation's assistant messages
 * @param {Record<string, string | string[]>} answers - The replayed answers
 * @returns {Message[]} A copy with the answers applied, or the same array when there is no message
 */
const applyReplayedAnswers = (messages: Message[], answers: Record<string, string | string[]>): Message[] => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) return messages;
  const updatedMessages = [...messages];
  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    segments: [...(lastMessage.segments ?? []), { type: 'user_answers', answers }],
    pendingQuestions: undefined,
    pendingJobId: undefined,
  };
  return updatedMessages;
};

/**
 * Rolls back a submit the backend refused: drops the optimistic `user_answers`
 * segment (or the approval's stamped decision), puts the prompt back in its
 * pending slot and records why, so the card is answerable again and shows the
 * reason inline.
 *
 * @param {Message[]} messages - The conversation's assistant messages
 * @param {PendingPromptSubmit} pending - The refused submit
 * @param {string} reason - The backend's message, shown on the card
 * @returns {Message[]} A copy with the prompt restored, or the same array when its message is gone
 */
const rollbackRejectedSubmit = (messages: Message[], pending: PendingPromptSubmit, reason: string): Message[] => {
  const promptRejection = reason || PROMPT_REJECTION_FALLBACK;
  if (pending.kind === 'answer') {
    const { answersSegment } = pending;
    const index = messages.findLastIndex((message) =>
      Boolean(answersSegment && message.segments?.includes(answersSegment)),
    );
    if (index === -1) return messages;
    const message = messages[index];
    const updatedMessages = [...messages];
    updatedMessages[index] = {
      ...message,
      segments: (message.segments ?? []).filter((segment) => segment !== answersSegment),
      pendingQuestions: pending.questions,
      pendingJobId: pending.jobId,
      promptRejection,
      isLoading: false,
    };
    return updatedMessages;
  }
  const { toolApproval } = pending;
  if (!toolApproval) return messages;
  const index = messages.findLastIndex((message) =>
    Boolean(
      message.segments?.some(
        (segment) => segment.type === 'tool_approval' && segment.toolApproval.jobId === pending.jobId,
      ),
    ),
  );
  if (index === -1) return messages;
  const message = messages[index];
  const segments = message.segments ?? [];
  const decidedIndex = segments.findLastIndex(
    (segment) => segment.type === 'tool_approval' && segment.toolApproval.jobId === pending.jobId && segment.decision,
  );
  const updatedMessages = [...messages];
  updatedMessages[index] = {
    ...message,
    segments: segments.map((segment, i) =>
      i === decidedIndex && segment.type === 'tool_approval' ? { ...segment, decision: undefined } : segment,
    ),
    pendingToolApproval: toolApproval,
    promptRejection,
    isLoading: false,
  };
  return updatedMessages;
};

export {
  applyReplayedAnswers,
  findPromptMessageIndex,
  isPromptSubmitAck,
  PROMPT_REJECTION_FALLBACK,
  rollbackRejectedSubmit,
};
export type { PendingPromptSubmit };
